import { useEffect, useState } from "react";

import { CrudFilter, useDataProvider } from "@refinedev/core";

import {
  type TinaSiteOption,
  sortSites,
} from "@/pages/tina/runs/components/site-picker/utils";

const PAGE_SIZE = 200;
const MAX_PAGES = 200;
const PAGE_CONCURRENCY = 4;

export interface UseTinaSitesResult {
  sites: TinaSiteOption[];
  isLoading: boolean;
  error: string | null;
}

/**
 * Loads every enabled Tina site (paginated, in concurrent batches) once.
 * The faceted run picker filters this in memory by content type / source /
 * country / city — so we fetch the full enabled set a single time.
 */
export function useTinaSites(): UseTinaSitesResult {
  const { getList } = useDataProvider()("tina");
  const [sites, setSites] = useState<TinaSiteOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    async function loadSites() {
      setIsLoading(true);
      setError(null);

      try {
        const enabledFilter: CrudFilter = {
          field: "enabled",
          operator: "eq",
          value: true,
        };

        const fetchPage = async (currentPage: number) => {
          const response = await getList?.({
            resource: "tina-sites",
            pagination: { currentPage, pageSize: PAGE_SIZE, mode: "server" },
            filters: [enabledFilter],
            sorters: [],
          });
          return {
            data: (response?.data ?? []) as TinaSiteOption[],
            total: response?.total,
          };
        };

        const firstPage = await fetchPage(1);
        const loaded: TinaSiteOption[] = [...firstPage.data];
        const total = firstPage.total ?? firstPage.data.length;

        if (firstPage.data.length > 0 && loaded.length < total) {
          const totalPages = Math.min(MAX_PAGES, Math.ceil(total / PAGE_SIZE));
          if (Math.ceil(total / PAGE_SIZE) > MAX_PAGES) {
            // eslint-disable-next-line no-console
            console.warn(
              `[use-tina-sites] total ${total} exceeds MAX_PAGES * PAGE_SIZE (${MAX_PAGES * PAGE_SIZE}); truncating.`,
            );
          }

          for (
            let batchStart = 2;
            batchStart <= totalPages;
            batchStart += PAGE_CONCURRENCY
          ) {
            const batchEnd = Math.min(
              batchStart + PAGE_CONCURRENCY - 1,
              totalPages,
            );
            const pageNumbers: number[] = [];
            for (let p = batchStart; p <= batchEnd; p += 1) pageNumbers.push(p);

            const results = await Promise.all(pageNumbers.map(fetchPage));
            let sawEmpty = false;
            for (const result of results) {
              if (result.data.length === 0) {
                sawEmpty = true;
                continue;
              }
              loaded.push(...result.data);
            }
            if (sawEmpty || loaded.length >= total) break;
          }
        }

        if (isMounted) setSites(sortSites(loaded));
      } catch (caught) {
        if (isMounted) {
          setError(
            caught instanceof Error
              ? caught.message
              : "Failed to load Tina sites",
          );
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    }

    loadSites();

    return () => {
      isMounted = false;
    };
  }, [getList]);

  return { sites, isLoading, error };
}
